import trackDesignerSelected, {
  ITrackDesignerSelected,
} from '@components/operations-client/shared/analytics/helpers/trackDesignerSelected';
import LoginRegisterModalContext from '@context/Modal/login-register-modal-context';
import UserContext from '@context/User/user-context';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import { useContext } from 'react';
import { useRouter } from 'next/router';
import {
  useAssign11StarDesignerMutation
} from '@graphql-gen/mutations/assign11StartDesigner.gen';

// Explicitly naming this as 'old' as we've been having stories to move
// away from this endpoint, but some logic still seems pretty attached
// to how this works (specially when it comes to having the $room variable
// being optional or not.)
const oldSetPreferredDesigner = async (
  designerId: string,
  designerTrack: ITrackDesignerSelected
) => {
  // the room_id= bit is a weird requirement. excluding it causes an error. leaving for now
  try {
    trackDesignerSelected(designerTrack);

    const params = 'room_id=';

    await fetch(`/rooms/set_preferred_designer/${designerId}`, {
      method: 'POST',
      body: new URLSearchParams(params),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  } catch (e) {
    throw new Error('That designer could not be booked.');
  }
};

const getRedirectUrl = (
  roomId: string | string[] | undefined,
  fromChangeDesigner: string | string[] | undefined,
  roomToRedirect?: string | string[] | undefined
) => {
  if (!!roomId && fromChangeDesigner) {
    return `/room/home/${roomId}?showChangeDesignerConfirmation=true`;
  }
  return `/rooms/select-package?designerSelected=true${
    roomToRedirect ? `&roomId=${roomToRedirect}` : ''
  }`;
};

const bookDesigner = async (
  designerId: string,
  designerTrack: ITrackDesignerSelected,
  onAssignDesigner: any,
  roomId: string | string[] | undefined,
  fromChangeDesigner: string | string[] | undefined,
  roomToRedirect?: string | string[] | undefined
) => {
  try {
    trackDesignerSelected(designerTrack);

    if (!fromChangeDesigner) {
      await oldSetPreferredDesigner(designerId, designerTrack);
    } else {
      await onAssignDesigner({
        variables: {
          roomId: `${roomId}`,
          designerId: `${designerId}`,
          reasonId: undefined,
          shouldNotifyDesignerChanged: false,
        },
      });
    }

    setTimeout(() => {
      const redirectUrl = getRedirectUrl(
        roomId,
        fromChangeDesigner,
        roomToRedirect
      );

      const win: Window = window;
      win.location = redirectUrl;
    }, 200);
  } catch (e: any) {
    throw new Error(`That designer could not be booked. ${e.message}`);
  }
};

export function useBookDesignerAndProceedToPurchase() {
  const { user } = useContext(UserContext);
  const { open: openLoginRegister } = useContext(LoginRegisterModalContext);
  const router = useRouter();
  const [assign11StarDesignerMutation, { loading: isChangingDesigner }] =
    useAssign11StarDesignerMutation();

  const { roomId, fromChangeDesigner, roomToRedirect } = router.query;

  const bookDesignerAndProceedToPurchase = async (
    designer: ITrackDesignerSelected
  ) => {
    const { firstName, lastName, id } = designer;
    analyticsTrack('track_designer_book', {
      designer_name: `${firstName} ${lastName}`,
      designer_id: id,
    });

    const doBookDesigner = async () => {
      await bookDesigner(
        id,
        designer,
        assign11StarDesignerMutation,
        roomId,
        fromChangeDesigner,
        roomToRedirect
      );
    };

    if (!user) {
      openLoginRegister({
        source: 'designer_profile',
        async onLoginSuccess() {
          await doBookDesigner();
        },
        async onRegisterSuccess() {
          await doBookDesigner();
        },
        magicLinkRedirectUrl: router.asPath,
      });
    } else if (user.role === 'customer') {
      await doBookDesigner();
    }
  };

  return {
    bookDesignerAndProceedToPurchase,
    isChangingDesigner,
  };
}

export default useBookDesignerAndProceedToPurchase;
